import React from 'react';
import { CupSodaIcon, FactoryIcon, GaugeIcon, PackageIcon, RecycleIcon, StoreIcon, TruckIcon } from 'lucide-react';
type Layer = {
  label: string;
  Icon: React.ElementType;
  width: number;
  note: string;
};
const DRINK_WIDTH = 17;
const drink: Layer = {
  label: 'The drink',
  Icon: CupSodaIcon,
  width: DRINK_WIDTH,
  note: 'What the person actually wants'
};
const overhead: Layer[] = [{
  label: 'Packaging',
  Icon: PackageIcon,
  width: 33,
  note: 'Bottle, can, label, case'
}, {
  label: 'Freight',
  Icon: TruckIcon,
  width: 46,
  note: 'Moving liquid across the country'
}, {
  label: 'Storage',
  Icon: FactoryIcon,
  width: 59,
  note: 'Warehouse and backroom space'
}, {
  label: 'Refrigeration',
  Icon: GaugeIcon,
  width: 71,
  note: 'Cold chain, running constantly'
}, {
  label: 'Retail margin',
  Icon: StoreIcon,
  width: 86,
  note: 'Every hand it passes through'
}, {
  label: 'Waste',
  Icon: RecycleIcon,
  width: 100,
  note: 'Material discarded after one sip'
}];
export function HiddenCostStack() {
  return <figure className="relative overflow-hidden rounded-3xl border border-slate-200 bg-slate-50 p-4 sm:p-5" aria-labelledby="hidden-cost-caption" data-id="element-1038" data-mp="3248imxzuvoyx">
      <div className="flex flex-wrap items-end justify-between gap-2" data-id="element-1039" data-mp="2s6ue9wr7iqmz">
        <div data-id="element-1040" data-mp="2s8nkxc1tlauz">
          <p className="text-[10px] font-bold uppercase tracking-[.18em] text-blue-600" data-id="element-1041" data-mp="2s4zrecwdkog9">Cost of one serving</p>
          <p className="mt-1 text-sm font-bold text-navy-950" data-id="element-1042" data-mp="3kvi3u4izo26c">Everything that stacks up before the first sip.</p>
        </div>
        <p className="rounded-full border border-blue-200 bg-white px-2.5 py-1 text-[9px] font-bold uppercase tracking-[.12em] text-blue-700" data-id="element-1043" data-mp="3pv4ec67fn4bg">6 layers of overhead</p>
      </div>

      <ol className="mt-4 space-y-1.5" aria-label="Layers added to a single beverage serving" data-id="element-1044" data-mp="3fwf14qqhe81j">
        <li className="grid grid-cols-[80px_1fr_96px] items-center gap-2 sm:grid-cols-[100px_1fr_140px]" data-id="element-1045" data-mp="1zi5b7s6lcb8l">
          <div className="flex min-w-0 items-center gap-1.5" data-id="element-1046" data-mp="24h03dkqg7c5d">
            <drink.Icon className="h-3.5 w-3.5 shrink-0 text-blue-600" aria-hidden="true" data-id="element-1047" data-mp="24ijkfdjxhr87" />
            <span className="truncate text-[10px] font-bold text-navy-950 sm:text-[11px]" data-id="element-1048" data-mp="axpzl46dvulu">{drink.label}</span>
          </div>
          <div className="relative h-7 overflow-hidden rounded-md border border-blue-200 bg-white" data-id="element-1049" data-mp="61sl73eokybp">
            <span className="pd3-cost-bar absolute inset-y-0 left-0 rounded-[5px] bg-blue-600" style={{
            ['--pd3-bar-w' as string]: `${drink.width}%`,
            animationDelay: '0ms'
          }} data-id="element-1050" data-mp="16dagkbyziji" />
          </div>
          <p className="text-[9px] font-semibold leading-tight text-blue-700 sm:text-[10px]" data-id="element-1051" data-mp="3je2z7323q0ii">{drink.note}</p>
        </li>

        {overhead.map((layer, index) => <li key={layer.label} className="grid grid-cols-[80px_1fr_96px] items-center gap-2 sm:grid-cols-[100px_1fr_140px]" data-id="element-1052" data-mp="3eh2q0j9yghn6" data-mpi={index}>
            <div className="flex min-w-0 items-center gap-1.5" data-id="element-1053" data-mp="1coelm6lkro5t">
              <layer.Icon className="h-3.5 w-3.5 shrink-0 text-slate-500" aria-hidden="true" data-id="element-1054" data-mp="1clioullgr5iy" />
              <span className="truncate text-[10px] font-bold text-navy-950 sm:text-[11px]" data-id="element-1055" data-mp="17mpc4ve03iju">{layer.label}</span>
            </div>
            <div className="relative h-7 overflow-hidden rounded-md border border-slate-200 bg-white" data-id="element-1056" data-mp="1k749ya94wicm">
              <span className="pd3-cost-bar absolute inset-y-0 left-0 rounded-[5px] bg-navy-950" style={{
            ['--pd3-bar-w' as string]: `${layer.width}%`,
            animationDelay: `${180 + index * 150}ms`
          }} data-id="element-1057" data-mp="1oxkra5g2so49" />
              <span className="absolute inset-y-0 border-l border-dashed border-blue-400/70" style={{
            left: `${DRINK_WIDTH}%`
          }} aria-hidden="true" data-id="element-1058" data-mp="wb02ncqziq4e" />
            </div>
            <p className="text-[9px] font-semibold leading-tight text-slate-500 sm:text-[10px]" data-id="element-1059" data-mp="167d1sq5atw6j">{layer.note}</p>
          </li>)}
      </ol>

      <div className="mt-3 flex items-center gap-2 rounded-xl border border-blue-200 bg-white px-3 py-2" data-id="element-1060" data-mp="1b4mr85sou7ud">
        <span className="h-2.5 w-2.5 shrink-0 rounded-sm bg-blue-600" aria-hidden="true" data-id="element-1061" data-mp="2hp4ee0fsmmbj" />
        <p className="text-[10px] font-semibold text-slate-600" data-id="element-1062" data-mp="2mnz6jsznhn8b">Beverage</p>
        <span className="ml-2 h-2.5 w-2.5 shrink-0 rounded-sm bg-navy-950" aria-hidden="true" data-id="element-1063" data-mp="2k7pzhfrggia2" />
        <p className="text-[10px] font-semibold text-slate-600" data-id="element-1064" data-mp="2g6xmqcjdjv91">Physical overhead</p>
      </div>

      <figcaption id="hidden-cost-caption" className="relative mt-2.5 rounded-xl bg-navy-950 px-3 py-2.5 text-center text-xs font-bold leading-snug text-white sm:text-sm" data-id="element-1065" data-mp="2u66ssjdv98za">
        The traditional system compounds cost and material at every handoff.
      </figcaption>
    </figure>;
}